/**
 * The contract between a page that needs a signed-in visitor and the login
 * page: `/login?next=/account` brings them back where they were.
 */
export const RETURN_PARAM = 'next'

/** A same-origin absolute path: one leading slash, and not `//host` or `/\host`. */
const SAME_ORIGIN_PATH = /^\/(?![/\\])/

/**
 * Where to send someone after they sign in, or null to use the page's default.
 *
 * Only a same-origin path is honoured. Anything carrying a scheme or a host —
 * including the protocol-relative `//evil.example` a browser would happily
 * follow — is dropped, so a crafted login link can never bounce a visitor off
 * the site after they have typed their password into it.
 *
 * The regex alone isn't enough: a browser strips tabs and newlines from a URL
 * before resolving it, so `/\t/evil.example` passes the regex but is navigated
 * to as `//evil.example`. So the value is also resolved with the browser's own
 * parser, kept only if it stays on this origin, and returned as the path that
 * was checked. That path is checked against the regex again, because dot
 * segments can resolve to one, such as `/..//evil.example` to `//evil.example`.
 */
export function readReturnPath(search: string): string | null {
  const value = new URLSearchParams(search).get(RETURN_PARAM)
  if (!value || !SAME_ORIGIN_PATH.test(value)) return null

  let url: URL
  try {
    url = new URL(value, window.location.origin)
  } catch {
    return null
  }
  if (url.origin !== window.location.origin) return null

  const path = url.pathname + url.search + url.hash
  return SAME_ORIGIN_PATH.test(path) ? path : null
}

/** The login page, remembering where the visitor was. */
export function loginHref(loginUrl: string, returnPath: string): string {
  const separator = loginUrl.includes('?') ? '&' : '?'
  return `${loginUrl}${separator}${RETURN_PARAM}=${encodeURIComponent(returnPath)}`
}
